import { ref, uploadBytes, getBytes } from "firebase/storage";
import { storage } from "./firebaseApp";
import {
  CACHE_MANIFEST,
  GalleryAlbumManifest,
  GalleryIndexManifest,
  INDEX_MANIFEST_PATH,
  MANIFEST_VERSION,
  albumManifestPath,
} from "./galleryPaths";
import { emptyAlbumManifest, patchIndex, summariseAlbum } from "./galleryManifestOps";

const readJson = async <T,>(path: string): Promise<T | null> => {
  try {
    const bytes = await getBytes(ref(storage, path));
    return JSON.parse(new TextDecoder().decode(bytes)) as T;
  } catch {
    // Absent, unreadable or malformed: callers rebuild from a clean slate.
    return null;
  }
};

const writeJson = async (path: string, value: unknown): Promise<void> => {
  const body = new Blob([JSON.stringify(value)], { type: "application/json" });
  await uploadBytes(ref(storage, path), body, {
    contentType: "application/json",
    cacheControl: CACHE_MANIFEST,
  });
};

export const readAlbumManifest = async (album: string): Promise<GalleryAlbumManifest> => {
  const found = await readJson<GalleryAlbumManifest>(albumManifestPath(album));
  if (found && found.version === MANIFEST_VERSION && Array.isArray(found.photos)) {
    return found;
  }
  return emptyAlbumManifest(album, new Date().toISOString());
};

/**
 * Has the backfill seeded the manifests yet? The index's existence is the
 * signal. Until it exists the clients are all on their listAll fallback, and
 * writing a partial index here would be actively harmful — see
 * syncAlbumManifest.
 */
const manifestsAreSeeded = async (): Promise<boolean> =>
  (await readJson<GalleryIndexManifest>(INDEX_MANIFEST_PATH)) !== null;

export const writeAlbumManifest = (manifest: GalleryAlbumManifest): Promise<void> =>
  writeJson(albumManifestPath(manifest.album), manifest);

export const readIndexManifest = async (): Promise<GalleryIndexManifest> => {
  const found = await readJson<GalleryIndexManifest>(INDEX_MANIFEST_PATH);
  if (found && found.version === MANIFEST_VERSION && Array.isArray(found.albums)) {
    return found;
  }
  return { version: MANIFEST_VERSION, generatedAt: new Date().toISOString(), albums: [] };
};

export const writeIndexManifest = (index: GalleryIndexManifest): Promise<void> =>
  writeJson(INDEX_MANIFEST_PATH, index);

/**
 * Apply a mutation to one album's manifest and reflect the result in the index.
 * Reads only that album's manifest — never walks the album's objects — so an
 * upload costs two reads and two writes regardless of album size.
 *
 * Manifest upkeep must never break a gallery operation: a failure here is
 * logged and swallowed, and the next backfill run repairs the drift.
 */
export const syncAlbumManifest = async (
  album: string,
  mutate: (manifest: GalleryAlbumManifest, at: string) => GalleryAlbumManifest
): Promise<void> => {
  try {
    // Before the backfill has run there is no index, and every client is on its
    // listAll fallback. Creating one here would publish an index naming a
    // single album — clients would switch to it and show 1 album instead of
    // 135. Stay out of the way until the backfill seeds the manifests.
    if (!(await manifestsAreSeeded())) {
      console.info(
        `Gallery manifests not seeded yet; skipping sync for "${album}". ` +
          "Run scripts/gallery-backfill to seed them."
      );
      return;
    }

    const at = new Date().toISOString();
    const updated = mutate(await readAlbumManifest(album), at);
    await writeAlbumManifest(updated);

    const index = await readIndexManifest();
    await writeIndexManifest(patchIndex(index, album, summariseAlbum(updated), at));
  } catch (error) {
    console.error(`Gallery manifest sync failed for "${album}":`, error);
  }
};
